import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs';
import { ENVIRONMENT } from '@base/lib/core/tokens/environment';
import { StorageService } from '@base/lib/core/services/storage';
import { PermissionService, Role } from '@base/lib/core/services/permission';

export interface PostAuthenticateSignRequest {
  clientType: 'web' | 'app';
  account: string;
  password: string;
}

export interface PostAuthenticateSignResponse {
  user: {
    id: number;
    account: string;
    identityNumber: string;
    name: string;
    birthday: string;
    phone: string;
    tel?: string;
    email?: string;
    sex: 'Male' | 'Female' | 'Other';
    age: number;
    imageUrl: string;
    recordDate: string;
    isCooperatorMember: boolean;
    cooperatorId: number;
    cooperatorName: string;
    userCertification: {
      generalMember: boolean;
      identityCertifiedMember: boolean;
      medicalSystemCertifiedMember: boolean;
      hrc: boolean;
    };
    roles: {
      roleCode: Role;
      roleName: string;
      hospitalOrganizationId: number;
      hospitalOrganizationCode: string;
      hospitalOrganizationName: string;
      serviceLocationCode: string | null;
      canUseWebService: boolean | null;
    }[];
    userDetail: {
      informationConsent?: boolean;
      healthPassbook?: boolean;
      agreeOrganizationSwitchData?: boolean;
    };
    userAddresses: {
      city: string;
      district: string;
      street: string;
      kind: number;
    }[];
  };
  accessToken: string;
  refreshToken: string;
  accessTokenExpireAt: string;
  deviceNonce: string;
}

export interface PostAuthenticateSignOutResponse {
  message: string;
}

export interface PostAuthenticateRefreshRequest {
  refreshToken?: string;
  fromTarget?: string;
}

export interface PostAuthenticateRefreshResponse {
  accessToken: string;
  refreshToken: string;
  accessTokenExpireAt: string;
  deviceNonce: string;
}

export interface PostAuthenticateRefreshRoleTokenRequest {
  refreshToken: string;
  userRole: Role;
}

export interface PostAuthenticateRefreshRoleTokenResponse {
  accessToken: string;
  refreshToken: string;
  accessTokenExpireAt: string;
  deviceNonce: string;
}

export const AUTH_TOKEN_KEY = 'auth_token';
export const CURRENT_ROLE_KEY = 'current_role';

export interface AuthToken {
  accessToken: string;
  expires: string;
  refreshToken: string;
  deviceNonce: string;
}

export interface PostAuthenticateSendVerificationCodeRequest {
  account: string;
}

export interface PostAuthenticateSendVerificationCodeResponse {
  code: number;
  message?: string;
  timestamp: string;
  payload?: {
    description: string;
  };
}

export interface PostAuthenticateVerifyCodeRequest {
  account: string;
  code: string;
}

export interface PostAuthenticateVerifyCodeResponse {
  code: number;
  message: string;
  timesyamp: string;
  payload: {
    resetToken: string;
  };
}

export interface PostAuthenticateResetPasswordWithTokenRequest {

}

export interface PostAuthenticateResetPasswordWithTokenResponse {

}

@Injectable({
  providedIn: 'root',
})
export class AuthenticateService {
  private http = inject(HttpClient);
  private storageService = inject(StorageService);
  private permissionService = inject(PermissionService);
  private readonly environment = inject(ENVIRONMENT);

  readonly baseUrl = signal(this.environment.api.baseUrl).asReadonly();

  isAuthenticated() {
    return Boolean(this.storageService.get<AuthToken>(AUTH_TOKEN_KEY)?.accessToken);
  };

  apiPostAuthenticateSign(request: PostAuthenticateSignRequest) {
    return this.http.post<PostAuthenticateSignResponse>(
      `${this.baseUrl()}/api/Authenticate/SignIn`,
      request,
    ).pipe(
      tap((res) => {
        const authToken = {
          accessToken: res.accessToken,
          expires: res.accessTokenExpireAt,
          refreshToken: res.refreshToken,
          deviceNonce: res.deviceNonce,
        };

        this.storageService.set<AuthToken>(AUTH_TOKEN_KEY, authToken);
        this.permissionService.updateCurrentRole(res.user.roles[0].roleCode);
      }),
    );
  }

  apiPostAuthenticateSignOut() {
    return this.http.post<null>(
      `${this.baseUrl()}/api/Authenticate/SignOut`,
      null,
    ).pipe(
      tap(() => {
        this.storageService.remove(AUTH_TOKEN_KEY);
        this.storageService.remove(CURRENT_ROLE_KEY);
        this.permissionService.updateCurrentRole('Citizen');
      }),
    );
  }

  apiPostAuthenticateRefresh(request: PostAuthenticateRefreshRequest) {
    return this.http.post<PostAuthenticateRefreshResponse>(
      `${this.baseUrl()}/api/Authenticate/Refresh`,
      request,
      {
        withCredentials: true,
      },
    ).pipe(
      tap((res) => {
        const authToken = {
          accessToken: res.accessToken,
          expires: res.accessTokenExpireAt,
          refreshToken: res.refreshToken,
          deviceNonce: res.deviceNonce,
        };

        this.storageService.set<AuthToken>(AUTH_TOKEN_KEY, authToken);
      }),
    );
  }

  apiPostAuthenticateRefreshRoleToken(request: PostAuthenticateRefreshRoleTokenRequest) {
    const newCurrentRole = request.userRole;

    return this.http.post<PostAuthenticateRefreshRoleTokenResponse>(
      `${this.baseUrl()}/api/Authenticate/refresh-role-token`,
      request,
      {
        withCredentials: true,
      },
    ).pipe(
      tap((res) => {
        const authToken = {
          accessToken: res.accessToken,
          expires: res.accessTokenExpireAt,
          refreshToken: res.refreshToken,
          deviceNonce: res.deviceNonce,
        };

        this.storageService.set<AuthToken>(AUTH_TOKEN_KEY, authToken);
        this.permissionService.updateCurrentRole(newCurrentRole);
      }),
    );
  }

  apiPostAuthenticateSendVerificationCode(request: PostAuthenticateSendVerificationCodeRequest) {
    return this.http.post<PostAuthenticateSendVerificationCodeResponse>(
      `${this.baseUrl()}/api/Authenticate/SendVerificationCode`,
      request,
    );
  }

  apiPostAuthenticateVerifyCode(request: PostAuthenticateVerifyCodeRequest) {
    return this.http.post<PostAuthenticateVerifyCodeResponse>(
      `${this.baseUrl()}/api/Authenticate/verify-code`,
      request,
    );
  }

  apiPostAuthenticateResetPasswordWithToken(request: PostAuthenticateResetPasswordWithTokenRequest) {
    return this.http.post<PostAuthenticateResetPasswordWithTokenResponse>(
      `${this.baseUrl()}POST/api/Authenticate/reset-password-with-token`,
      request,
    );
  }
}
